<template>
  <div class="home">
    <div>
      <section>
        <div class="py-8 px-8">
          <div class="d-flex flex-wrap align-items-center justify-content-between">
            <div class="col-12 col-lg-auto mb-3 mb-lg-0">
              <div class="d-flex align-items-center">
                <h4 class="mb-0 me-2">{{ $t('my_tasks') }}</h4>
              </div>
            </div>
            <div class="col-12 col-lg-auto d-flex align-items-center gap-2">
            
              <!-- Simple Search -->
              <div class="input-group flex-grow-1" style="border-bottom: 1px solid #ddd;">
                <input class="form-control border-0 search-input" autocomplete="off" type="text" v-model="filters.search" :placeholder="$t('search')">
              </div>



              <!-- View Dropdown -->
              <div class="position-relative d-inline-block">
                <button @click="showViewMenu = !showViewMenu" class="btn btn-outline-primary" style="padding:0.3rem 0.5rem">
                  <AppIcon :name="currentViewIcon" :class="currentViewIcon" />
                </button>
                <div v-if="showViewMenu" class="dropdown-menu show" style="position: absolute; right: 0; z-index: 1000; min-width: 150px;">
                  <a class="dropdown-item" href="#" @click.prevent="switchView('list')">
                    <AppIcon name="bi-list-ul" class="bi bi-list-ul me-2" /> {{$t('list')}}
                  </a>
                  <a class="dropdown-item" href="#" @click.prevent="switchView('kanban')">
                    <AppIcon name="bi-kanban" class="bi bi-kanban me-2" /> {{$t('kanban')}}
                  </a>
                  <a class="dropdown-item" href="#" @click.prevent="switchView('calendar')">
                    <AppIcon name="bi-calendar" class="bi bi-calendar me-2" /> {{$t('calendar')}}
                  </a>

                </div>
                <div v-if="showViewMenu" @click="showViewMenu = false" style="position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; z-index: 999;"></div>
              </div>

              <!-- Stats Toggle -->
              <button @click="showStats = !showStats" class="btn btn-outline-primary" :class="{ 'active': showStats }" style="padding:0.3rem 0.5rem" :title="$t('toggle_stats')">
                <AppIcon name="bi-bar-chart" class="bi bi-bar-chart" />
              </button>

              <!-- Advanced Search Toggle -->
              <button @click="toggleAdvancedSearch" class="btn btn-outline-primary" style="padding:0.3rem 0.5rem" :class="{ 'active': showAdvancedSearch }">
                <AppIcon name="bi-funnel" class="bi bi-funnel" />
              </button>
            </div>
            
          </div>

          <div class="row mt-4">
            <!-- Spacer or other content if needed, otherwise empty -->
          </div>
          <!-- Advanced Search Filters -->
          <div v-if="showAdvancedSearch" class="mt-4 p-4 advanced-search-section">
            <div class="row g-3">
              <div class="col-md-3">
                <label class="form-label">{{$t('status')}}</label>
                <select v-model="filters.status" class="form-select filter-input">
                  <option value="">{{$t('all')}}</option>
                  <option v-for="status in availableStatuses" :key="status.value" :value="status.value">{{ status.label }}</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">{{$t('priority')}}</label>
                <select v-model="filters.priority" class="form-select filter-input">
                  <option value="">{{$t('all')}}</option>
                  <option value="low">{{$t('low')}}</option>
                  <option value="medium">{{$t('medium')}}</option>
                  <option value="high">{{$t('high')}}</option>
                  <option value="urgent">{{$t('urgent')}}</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">{{$t('due_date_range')}}</label>
                <select v-model="dateRangeOption" @change="handleDateRangeChange" class="form-select">
                  <option value="">{{$t('filter_by_date')}}</option>
                  <option value="today">{{$t('today')}}</option>
                  <option value="yesterday">{{$t('yesterday')}}</option>
                  <option value="this_week">{{$t('this_week')}}</option>
                  <option value="last_week">{{$t('last_week')}}</option>
                  <option value="this_month">{{$t('this_month')}}</option>
                  <option value="custom">{{$t('custom_date')}}</option>
                </select>
              </div>
              <div class="col-md-3" v-if="dateRangeOption === 'custom'">
                <label class="form-label">&nbsp;</label>
                <date-picker 
                  :clearable="true" 
                  :editable="false" 
                  class="w-100" 
                  v-model="range" 
                  type="date" 
                  range 
                  :multi-calendars="1" 
                  :placeholder="$t('select_date_range')"
                ></date-picker>
              </div>
              <div class="col-12 d-flex justify-content-end">
                <button @click="clearFilters" class="btn btn-secondary me-2">
                  <AppIcon name="bi-x-circle" class="bi bi-x-circle" />
                  {{$t('clear_filters')}}
                </button>
                <button @click="applyFilters" class="btn btn-primary">
                  <AppIcon name="bi-search" class="bi bi-search" />
                  {{$t('apply_filters')}}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Stats Cards -->
      <section class="">
        <div class="px-8">
          <div  v-if="showStats" class="card mb-3 border shadow-sm">
            <div class="card-body py-2 px-2">
              <div class="d-flex justify-content-between align-items-center text-center">
                <div class="flex-fill border-end">
                    <h6 class="mb-0 fw-bold">{{ stats.total }}</h6>
                    <small class="text-muted text-uppercase" style="font-size: 0.65rem">{{$t('total')}}</small>
                </div>
                <div class="flex-fill border-end">
                    <h6 class="mb-0 fw-bold text-warning">{{ stats.pending }}</h6>
                    <small class="text-muted text-uppercase" style="font-size: 0.65rem">{{$t('pending')}}</small>
                </div>
                <div class="flex-fill border-end">
                    <h6 class="mb-0 fw-bold text-primary">{{ stats.in_progress }}</h6>
                    <small class="text-muted text-uppercase" style="font-size: 0.65rem">{{$t('in_progress')}}</small>
                </div>
                <div class="flex-fill border-end">
                    <h6 class="mb-0 fw-bold text-info">{{ stats.review }}</h6>
                    <small class="text-muted text-uppercase" style="font-size: 0.65rem">{{$t('review')}}</small>
                </div>
                <div class="flex-fill border-end">
                    <h6 class="mb-0 fw-bold text-success">{{ stats.completed }}</h6>
                    <small class="text-muted text-uppercase" style="font-size: 0.65rem">{{$t('completed')}}</small>
                </div>
                <div class="flex-fill">
                    <h6 class="mb-0 fw-bold text-danger">{{ stats.overdue }}</h6>
                    <small class="text-muted text-uppercase" style="font-size: 0.65rem">{{$t('overdue')}}</small>
                </div>
              </div>
            </div>
          </div>

          <div class="page-table-container main-table-container mb-6 rounded">
            
            <!-- table -->
            <div class="table-responsive" style="background:transparent !important">
              <!-- List View -->
              <div v-if="taskView === 'list'">
                <table class="table mb-0 table-borderless table-striped small">
                  <thead>
                    <tr class="text-secondary">
                      <sortable-table-header field="title" label="title" :sort-field="sortField" :sort-direction="sortDirection" @sort="toggleSort" />
                      <sortable-table-header field="project" label="project" :sort-field="sortField" :sort-direction="sortDirection" @sort="toggleSort" />
                      <sortable-table-header field="status" label="status" :sort-field="sortField" :sort-direction="sortDirection" @sort="toggleSort" />
                      <sortable-table-header field="priority" label="priority" :sort-field="sortField" :sort-direction="sortDirection" @sort="toggleSort" />
                      <sortable-table-header field="progress" label="progress" :sort-field="sortField" :sort-direction="sortDirection" @sort="toggleSort" />
                      <sortable-table-header field="due_date" label="due_date" :sort-field="sortField" :sort-direction="sortDirection" @sort="toggleSort" />
                      <th class="pt-4 pb-3 px-6">{{$t('action')}}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="assignment in sortedTasks" :key="assignment.id">
                      <td class="py-5 px-6"  @click="viewTask(assignment.task)" style="cursor: pointer;text-decoration: underline">
                        <strong>{{ assignment.task.title }}</strong>
                      </td>
                      <td class="py-5 px-6">{{ assignment.task.project ? assignment.task.project.name : '-' }}</td>
                      <td class="py-5 px-6">
                        <span class="badge" :style="{ backgroundColor: getStatusColor(assignment.task.status), color: '#fff' }">
                          {{ getStatusLabel(assignment.task.status) }}
                        </span>
                      </td>
                      <td class="py-5 px-6">
                        <span :class="'badge bg-' + getPriorityColor(assignment.task.priority)">
                          {{ $t(assignment.task.priority) }}
                        </span>
                      </td>
                      <td class="py-5 px-6">
                        <div class="progress" style="height: 20px; min-width: 80px;">
                          <div class="progress-bar" :style="{width: assignment.task.progress + '%'}">
                            {{ assignment.task.progress }}%
                          </div>
                        </div>
                      </td>
                      <td class="py-5 px-6">
                        <span :class="{'text-danger': isOverdue(assignment.task.due_date)}">
                          {{ formatDate(assignment.task.due_date) }}
                        </span>
                      </td> 
                      <td class="py-5 px-6">
                        <button @click="viewTask(assignment.task)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                          <AppIcon name="bi-eye" class="bi bi-eye" /> {{$t('view')}}
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Kanban View -->
              <my-task-kanban v-else-if="taskView === 'kanban'" :tasks="tasks" @task-updated="calculateStats" @view="viewTask"></my-task-kanban>

              <!-- Calendar View -->
              <my-task-calendar v-else-if="taskView === 'calendar'" :tasks="tasks" @refresh="loadTasks" @view="viewTask"></my-task-calendar>
            </div>
            <!-- table -->
            <table-loading-bar v-if="loading" class="page-table-state text-center" />
            <div v-if="loading === false && !tasks.length" class="page-table-state text-center" style="padding:1rem">{{$t('no_tasks_assigned')}}.</div>

          </div>

        </div>
      </section>
    </div>

    <!-- Task Detail Modal -->
    <task-detail-modal v-if="selectedTask" :task="selectedTask" @close="selectedTask = null" @refresh="loadTasks"></task-detail-modal>
  </div>
</template>

<script>
import helper from '../../helper'
import moment from 'moment'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import MyTaskKanban from '../../components/task/MyTaskKanban.vue'
import MyTaskCalendar from '../../components/task/MyTaskCalendar.vue'
import TaskDetailModal from '../../components/project/TaskDetailModal.vue'
import { getTaskStatuses, getTaskStatusColor, getTaskStatusLabel } from '../../helpers/taskStatusHelper'
import sortableList from '../../mixins/sortableList'

export default {
  name: 'MyTasks',
  persistedFilterState: {
    key: 'my-tasks',
    fields: ['filters', 'dateRangeOption', 'range', 'sortField', 'sortDirection'],
    dateFields: ['range'],
  },
  mixins: [sortableList],
  components: {
    DatePicker,
    MyTaskKanban,
    MyTaskCalendar,
    TaskDetailModal
  },
  data() {
    return {
      helper,
      tasks: [],
      loading: false,
      taskView: 'kanban',
      selectedTask: null,
      filters: {
        status: '',
        priority: '',
        search: ''
      },
      range: [],
      showAdvancedSearch: false,
      showViewMenu: false,
      showStats: false,
      dateRangeOption: '',
      searchTimeout: null,
      stats: {
        total: 0,
        pending: 0,
        in_progress: 0,
        review: 0,
        completed: 0,
        overdue: 0
      }
    }
  },
  computed: {
    currentViewIcon() {
      const icons = {
        list: 'bi bi-list-ul',
        kanban: 'bi bi-kanban',
        calendar: 'bi bi-calendar'
      }
      return icons[this.taskView] || 'bi bi-list-ul'
    },
    validTasks() {
      return this.tasks.filter(assignment => assignment.task !== null)
    },
    sortedTasks() {
      return this.sortRowsBy(this.validTasks, assignment => {
        if (this.sortField === 'project') return assignment.task.project?.name
        return assignment.task?.[this.sortField]
      }, this.sortDirection)
    },
    availableStatuses() {
      return getTaskStatuses(this.$store);
    }
  },
  mounted() {
    //check permissions
    if(helper.checkAccess('view_tasks') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_tasks') == -2) return this.$router.push({path : '/subscribe'})

    window.mixpanel.track("my_tasks");
    // Initialize this_month date range
    // this.handleDateRangeChange()
    this.loadTasks()
  },
  methods: {
    async loadTasks() {
      this.loading = true
      try {
        // Build query parameters
        let url = `${process.env.VUE_APP_API}/tasks/my-tasks?`
        const params = []
        
        if (this.filters.status) {
          params.push(`status=${this.filters.status}`)
        }
        if (this.filters.priority) {
          params.push(`priority=${this.filters.priority}`)
        }
        if (this.filters.search) {
          params.push(`search=${this.filters.search}`)
        }
        if (this.range.length && this.range[0] && this.range[1]) {
          params.push(`dateFrom=${moment(this.range[0]).format('YYYY-MM-DD')}`)
          params.push(`dateTo=${moment(this.range[1]).format('YYYY-MM-DD')}`)
        }
        
        url += params.join('&')
        
        const response = await helper.customGet(url)
        this.tasks = response.data
        this.calculateStats()
      } catch (error) {
        alert('Error loading tasks:', error)
        helper.showToast(this.$t('error_loading_tasks'), 'error')
      } finally {
        this.loading = false
      }
    },
    calculateStats() {
      // Filter out null tasks first
      const validTasks = this.tasks.filter(a => a.task !== null)
      
      this.stats.total = validTasks.length
      this.stats.pending = validTasks.filter(a => a.task.status === 'todo').length
      this.stats.in_progress = validTasks.filter(a => a.task.status === 'in_progress').length
      this.stats.review = validTasks.filter(a => a.task.status === 'review').length
      this.stats.completed = validTasks.filter(a => a.task.status === 'completed').length
      
      const now = new Date()
      this.stats.overdue = validTasks.filter(a => {
        if (!a.task.due_date || a.task.status === 'completed') return false
        return new Date(a.task.due_date) < now
      }).length
    },
    async viewTask(task) {
      if (helper.checkAccess('view_tasks') <= 0) return
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/tasks/${task.id}`)
        this.selectedTask = response.data
      } catch (error) {
        alert('Error loading task details:', error)
        helper.showToast(this.$t('error_loading_task'), 'error')
      }
    },
    getStatusColor(status) {
      return getTaskStatusColor(this.$store, status);
    },
    getStatusLabel(status) {
      return getTaskStatusLabel(this.$store, status);
    },
    getPriorityColor(priority) {
      const colors = {
        low: 'info',
        medium: 'primary',
        high: 'warning',
        urgent: 'danger'
      }
      return colors[priority] || 'primary'
    },
    formatDate(date) {
      if (!date) return this.$t('not_set')
      return new Date(date).toLocaleDateString()
    },
    isOverdue(dueDate) {
      if (!dueDate) return false
      return new Date(dueDate) < new Date()
    },
    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadTasks()
      }, 500)
    },
    toggleAdvancedSearch() {
      this.showAdvancedSearch = !this.showAdvancedSearch
    },
    clearFilters() {
      this.filters = {
        status: '',
        priority: '',
        search: ''
      }
      this.range = []
      this.loadTasks()
    },
    applyFilters() {
      this.loadTasks()
    },
    // Handle date range selection
    handleDateRangeChange() {
      if (this.dateRangeOption === '') {
        // All dates - clear range
        this.range = []
        this.loadTasks()
      } else if (this.dateRangeOption === 'today') {
        const start = new Date()
        const end = new Date()
        this.range = [start, end]
      } else if (this.dateRangeOption === 'yesterday') {
        const start = new Date()
        const end = new Date()
        start.setTime(start.getTime() - 3600 * 1000 * 24)
        end.setTime(end.getTime() - 3600 * 1000 * 24)
        this.range = [start, end]
      } else if (this.dateRangeOption === 'this_week') {
        const start = new Date()
        const end = new Date()
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
        this.range = [start, end]
      } else if (this.dateRangeOption === 'last_week') {
        const start = new Date()
        const end = new Date()
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 14)
        end.setTime(end.getTime() - 3600 * 1000 * 24 * 7)
        this.range = [start, end]
      } else if (this.dateRangeOption === 'this_month') {
        const start = new Date()
        const end = new Date()
        start.setDate(1)
        this.range = [start, end]
      } else if (this.dateRangeOption === 'custom') {
        // Don't set range yet, wait for user to pick dates
        this.range = []
      }
    },
    switchView(view) {
      this.taskView = view
      this.showViewMenu = false
    }
  },
  watch: {
    'filters.status'() {
      if (this.showAdvancedSearch) {
        this.debounceSearch()
      }
    },
    'filters.priority'() {
      if (this.showAdvancedSearch) {
        this.debounceSearch()
      }
    },
    'filters.search'() {
      if (this.filters.search.length > 2 || this.filters.search.length === 0) {
        this.debounceSearch()
      }
    },
    range() {
      if (this.showAdvancedSearch && this.range.length && this.range[0] && this.range[1]) {
        this.loadTasks()
      } else if (this.showAdvancedSearch && this.range[0] == null) {
        this.loadTasks()
      }
    }
  }
}
</script>

<style scoped>
.table-borderless>:not(caption)>*>* {
  padding: 5px !important
}

.gap-2 {
  gap: 0.5rem;
}

.stats-card {
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
}

.stats-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.stats-card .card-body {
  padding: 0.5rem !important;
}

.stats-icon {
  font-size: 1.25rem;
  margin-bottom: 0.25rem;
}

.stats-card h5 {
  font-weight: bold;
  font-size: 1.25rem;
  margin-bottom: 0.25rem;
}

.stats-card small {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  display: block;
  line-height: 1.2;
}

.advanced-search-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.filter-input {
  transition: all 0.3s ease;
}

.filter-input:focus {
  border-color: #303030;
  box-shadow: 0 0 0 0.2rem rgba(83, 77, 255, 0.25);
}

.btn-outline-primary {
  border-color: #cbd5e1;
  color: #334155;
}

.btn-outline-primary:hover {
  background-color: #004aad;
  border-color: #004aad;
  color: white;
}

.btn-outline-primary.active {
  background-color: #004aad;
  border-color: #004aad;
  color: white;
}
</style>
